import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  Pencil,
  Save,
  X,
  Lock,
  ArrowLeft,
  Eye,
  Loader2,
} from "lucide-react";
import Navbar from "./landing/Navbar";
import Footer from "./landing/Footer";
import { walkinDriveService } from "../services/walkinDriveService";

/* ------------------------------- helpers -------------------------------- */

const linesToArray = (str) =>
  (str || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const arrayToLines = (arr) => (arr || []).join("\n");

// Key skills accept either commas or newlines.
const tokensToArray = (str) =>
  (str || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

const EMPTY_FORM = {
  slug: null, // null => create, set => edit
  title: "",
  company: "",
  venue: "",
  city: "",
  status: "active",
  salaryDisplay: "",
  salaryMin: "",
  salaryMax: "",
  startDate: "",
  endDate: "",
  contactPerson: "",
  mapUrl: "",
  naukriUrl: "",
  dates: "",
  time: "",
  place: "",
  metaRole: "",
  metaIndustry: "",
  metaDepartment: "",
  metaRoleCategory: "",
  metaEmploymentType: "",
  metaEducation: "",
  metaExperience: "",
  overview: "",
  rolesOpen: "",
  responsibilities: [],
  requirements: "",
  perks: "",
  walkInDetails: "",
  importantNote: "",
  keySkills: "",
  about: "",
};

const driveToForm = (d) => ({
  slug: d.slug,
  title: d.title || "",
  company: d.company || "",
  venue: d.venue || "",
  city: d.city || "",
  status: d.status || "active",
  salaryDisplay: d.salary || "",
  salaryMin: d.salaryMinLpa ?? "",
  salaryMax: d.salaryMaxLpa ?? "",
  startDate: d.startDate || "",
  endDate: d.endDate || "",
  contactPerson: d.contactPerson || "",
  mapUrl: d.mapUrl || "",
  naukriUrl: d.naukriUrl || "",
  dates: d.timeAndVenue?.dates || "",
  time: d.timeAndVenue?.time || "",
  place: d.timeAndVenue?.place || "",
  metaRole: d.meta?.role || "",
  metaIndustry: d.meta?.industry || "",
  metaDepartment: d.meta?.department || "",
  metaRoleCategory: d.meta?.roleCategory || "",
  metaEmploymentType: d.meta?.employmentType || "",
  metaEducation: d.meta?.education || "",
  metaExperience: d.meta?.experience || "",
  overview: arrayToLines(d.overview),
  rolesOpen: arrayToLines(d.rolesOpen),
  responsibilities: (d.responsibilities || []).map((g) => ({
    title: g.title || "",
    items: arrayToLines(g.items),
  })),
  requirements: arrayToLines(d.requirements),
  perks: arrayToLines(d.perks),
  walkInDetails: d.walkInDetails || "",
  importantNote: d.importantNote || "",
  keySkills: (d.keySkills || []).join("\n"),
  about: d.about || "",
});

const formToPayload = (f) => {
  const meta = {
    role: f.metaRole.trim(),
    industry: f.metaIndustry.trim(),
    department: f.metaDepartment.trim(),
    roleCategory: f.metaRoleCategory.trim(),
    employmentType: f.metaEmploymentType.trim(),
    education: f.metaEducation.trim(),
    experience: f.metaExperience.trim(),
  };

  const timeAndVenue =
    f.dates.trim() || f.time.trim() || f.place.trim()
      ? { dates: f.dates.trim(), time: f.time.trim(), place: f.place.trim() }
      : null;

  const responsibilities = (f.responsibilities || [])
    .map((g) => ({ title: g.title.trim(), items: linesToArray(g.items) }))
    .filter((g) => g.title || g.items.length > 0);

  const meta_data = {
    salary: f.salaryDisplay.trim(),
    mapUrl: f.mapUrl.trim(),
    naukriUrl: f.naukriUrl.trim(),
    contactPerson: f.contactPerson.trim(),
    timeAndVenue,
    overview: linesToArray(f.overview),
    meta,
    rolesOpen: linesToArray(f.rolesOpen),
    responsibilities,
    requirements: linesToArray(f.requirements),
    perks: linesToArray(f.perks),
    walkInDetails: f.walkInDetails.trim(),
    importantNote: f.importantNote.trim(),
    keySkills: tokensToArray(f.keySkills),
    about: f.about.trim(),
  };

  return {
    title: f.title.trim(),
    company: f.company.trim(),
    venue: f.venue.trim(),
    city: f.city.trim() || null,
    salary_min_lpa: f.salaryMin !== "" ? Number(f.salaryMin) : null,
    salary_max_lpa: f.salaryMax !== "" ? Number(f.salaryMax) : null,
    start_date: f.startDate || null,
    end_date: f.endDate || null,
    status: f.status,
    meta_data,
  };
};

/* ---------------------------- field primitives -------------------------- */

const inputCls =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-ink-900 " +
  "focus:outline-none focus:ring-2 focus:ring-brand-purple/40 focus:border-brand-purple";

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-wide text-ink-500 mb-1.5">
      {label}
      {required && <span className="text-red-500"> *</span>}
      {hint && <span className="ml-1 normal-case font-medium text-ink-300">{hint}</span>}
    </span>
    {children}
  </label>
);

const Text = ({ value, onChange, ...rest }) => (
  <input className={inputCls} value={value} onChange={onChange} {...rest} />
);

const Area = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    className={inputCls + " resize-y"}
    rows={rows}
    value={value}
    onChange={onChange}
    {...rest}
  />
);

/* --------------------------------- page --------------------------------- */

export default function WalkinDriveAdmin() {
  const navigate = useNavigate();
  const isAuthed = useMemo(() => {
    if (typeof window === "undefined") return false;
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const role = (payload.role || "").toLowerCase();
      return role === "admin" || role === "walkin_admin";
    } catch (e) {
      return false;
    }
  }, []);

  const [drives, setDrives] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const isEditing = Boolean(form.slug);

  const loadDrives = async () => {
    try {
      setListLoading(true);
      const data = await walkinDriveService.list();
      setDrives(data);
    } catch (e) {
      setMessage({ type: "error", text: "Failed to load existing drives." });
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthed) loadDrives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setMessage(null);
  };

  const startEdit = (drive) => {
    setForm(driveToForm(drive));
    setMessage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addRespGroup = () =>
    setForm((f) => ({
      ...f,
      responsibilities: [...f.responsibilities, { title: "", items: "" }],
    }));

  const updateRespGroup = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      responsibilities: f.responsibilities.map((g, i) =>
        i === idx ? { ...g, [key]: value } : g
      ),
    }));

  const removeRespGroup = (idx) =>
    setForm((f) => ({
      ...f,
      responsibilities: f.responsibilities.filter((_, i) => i !== idx),
    }));

  const friendlyError = (err) => {
    const status = err?.response?.status;
    if (status === 401) return "Your session expired. Please sign in again.";
    if (status === 403)
      return "You don't have permission to manage walk-in drives (walkin_admin role required).";
    return err?.response?.data?.detail || "Something went wrong. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.company.trim() || !form.venue.trim()) {
      setMessage({
        type: "error",
        text: "Title, Company and Venue are required.",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payload = formToPayload(form);
    try {
      setSaving(true);
      setMessage(null);
      if (isEditing) {
        await walkinDriveService.update(form.slug, payload);
        setMessage({ type: "success", text: "Drive updated successfully." });
      } else {
        await walkinDriveService.create(payload);
        setMessage({ type: "success", text: "Drive created successfully." });
      }
      await loadDrives();
      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (drive) => {
    if (
      !window.confirm(
        `Delete "${drive.title}"? This cannot be undone.`
      )
    )
      return;
    try {
      await walkinDriveService.remove(drive.slug);
      if (form.slug === drive.slug) resetForm();
      await loadDrives();
      setMessage({ type: "success", text: "Drive deleted." });
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
    }
  };

  /* --------------------------- auth gate --------------------------- */
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Navbar
          onSignIn={() => navigate("/login")}
          onPostJob={() =>
            window.open("https://recruiter.jobxbharat.com/", "_blank")
          }
        />
        <main className="flex-1 pt-[64px] md:pt-[72px] flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center rounded-2xl border border-gray-200 bg-white p-10 shadow-sm">
            <div className="mx-auto w-12 h-12 rounded-full bg-brand-purple/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-brand-purple" />
            </div>
            <h1 className="mt-5 font-display text-2xl font-black text-ink-900">
              Internal access only
            </h1>
            <p className="mt-2 text-sm text-ink-500">
              Sign in with your internal team account to add or manage walk-in
              drives.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black tracking-wide
                         text-white bg-brand-saffron hover:brightness-110 active:scale-95 transition-all shadow-saffron"
            >
              Sign in
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ------------------------------ main ------------------------------ */
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Navbar
        onSignIn={() => navigate("/login")}
        onPostJob={() =>
          window.open("https://recruiter.jobxbharat.com/", "_blank")
        }
      />

      <header className="pt-[64px] md:pt-[72px]">
        <div className="bg-gradient-to-br from-brand-purple via-brand-deep to-brand-purple text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <Link
              to="/walkin-drives"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              View public walk-in drives
            </Link>
            <h1 className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl font-black leading-tight">
              Walk-in Drives — Admin
            </h1>
            <p className="mt-2 text-sm text-white/80 max-w-2xl">
              Add, edit and remove the walk-in drives shown on the public page.
            </p>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
          {/* ------------------------- form ------------------------- */}
          <form onSubmit={handleSubmit} className="space-y-7 order-2 lg:order-1">
            {message && (
              <div
                className={
                  "rounded-xl border p-4 text-sm font-semibold " +
                  (message.type === "success"
                    ? "border-brand-green/30 bg-brand-green/5 text-brand-green"
                    : "border-red-200 bg-red-50 text-red-700")
                }
              >
                {message.text}
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-black text-ink-900">
                {isEditing ? "Edit drive" : "Add a new drive"}
              </h2>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-ink-500 hover:text-ink-900"
                >
                  <X className="w-4 h-4" /> Cancel edit
                </button>
              )}
            </div>

            {/* Basics */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-ink-300">
                Basics
              </h3>
              <Field label="Job title" required>
                <Text value={form.title} onChange={set("title")} placeholder="Mega Walk-in Drive — Customer Support" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company" required>
                  <Text value={form.company} onChange={set("company")} placeholder="Tech Mahindra" />
                </Field>
                <Field label="Venue" required>
                  <Text value={form.venue} onChange={set("venue")} placeholder="Bahadurpally, Hyderabad" />
                </Field>
                <Field label="City" hint="(used for search/filters)">
                  <Text value={form.city} onChange={set("city")} placeholder="Hyderabad" />
                </Field>
                <Field label="Status">
                  <select className={inputCls} value={form.status} onChange={set("status")}>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="expired">Expired</option>
                  </select>
                </Field>
              </div>
            </section>

            {/* Salary & dates */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-ink-300">
                Salary & dates
              </h3>
              <Field label="Salary (display text)" hint="e.g. ₹3 – 5.5 LPA">
                <Text value={form.salaryDisplay} onChange={set("salaryDisplay")} placeholder="₹3 – 5.5 LPA" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Salary min (LPA)" hint="(number, optional)">
                  <Text type="number" step="0.1" value={form.salaryMin} onChange={set("salaryMin")} placeholder="3" />
                </Field>
                <Field label="Salary max (LPA)" hint="(number, optional)">
                  <Text type="number" step="0.1" value={form.salaryMax} onChange={set("salaryMax")} placeholder="5.5" />
                </Field>
                <Field label="Start date">
                  <Text type="date" value={form.startDate} onChange={set("startDate")} />
                </Field>
                <Field label="End date" hint="(drive closes after this)">
                  <Text type="date" value={form.endDate} onChange={set("endDate")} />
                </Field>
              </div>
            </section>

            {/* Time & venue / contact */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-ink-300">
                Time, venue & contact
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Dates (display)" hint="e.g. 22nd June – 10th July">
                  <Text value={form.dates} onChange={set("dates")} />
                </Field>
                <Field label="Time (display)" hint="e.g. 11:00 AM – 12:30 PM">
                  <Text value={form.time} onChange={set("time")} />
                </Field>
              </div>
              <Field label="Full venue / place">
                <Area value={form.place} onChange={set("place")} rows={2} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Contact person">
                  <Text value={form.contactPerson} onChange={set("contactPerson")} placeholder="Aniketh (98765…)" />
                </Field>
                <Field label="Map URL">
                  <Text value={form.mapUrl} onChange={set("mapUrl")} placeholder="https://maps.app.goo.gl/…" />
                </Field>
              </div>
              <Field label="Naukri / listing URL" hint="(shown if no map URL)">
                <Text value={form.naukriUrl} onChange={set("naukriUrl")} />
              </Field>
            </section>

            {/* Quick facts (meta) */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-ink-300">
                Quick facts
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Role">
                  <Text value={form.metaRole} onChange={set("metaRole")} placeholder="Technical Support – Voice" />
                </Field>
                <Field label="Industry">
                  <Text value={form.metaIndustry} onChange={set("metaIndustry")} placeholder="IT Services & Consulting" />
                </Field>
                <Field label="Department">
                  <Text value={form.metaDepartment} onChange={set("metaDepartment")} placeholder="Customer Success & Ops" />
                </Field>
                <Field label="Role category">
                  <Text value={form.metaRoleCategory} onChange={set("metaRoleCategory")} placeholder="Voice / Blended" />
                </Field>
                <Field label="Employment type">
                  <Text value={form.metaEmploymentType} onChange={set("metaEmploymentType")} placeholder="Full Time, Permanent" />
                </Field>
                <Field label="Education">
                  <Text value={form.metaEducation} onChange={set("metaEducation")} placeholder="Graduation Not Required" />
                </Field>
                <Field label="Experience">
                  <Text value={form.metaExperience} onChange={set("metaExperience")} placeholder="0 – 5 Years / Freshers" />
                </Field>
              </div>
            </section>

            {/* Long-form content */}
            <section className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-ink-300">
                Description & lists
              </h3>
              <Field label="Job description / overview" hint="(one paragraph per line)">
                <Area value={form.overview} onChange={set("overview")} rows={4} />
              </Field>
              <Field label="Roles open" hint="(one per line)">
                <Area value={form.rolesOpen} onChange={set("rolesOpen")} rows={3} />
              </Field>

              {/* Responsibilities groups */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="block text-xs font-bold uppercase tracking-wide text-ink-500">
                    Key responsibilities
                  </span>
                  <button
                    type="button"
                    onClick={addRespGroup}
                    className="inline-flex items-center gap-1 text-xs font-bold text-brand-purple hover:underline"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add group
                  </button>
                </div>
                <div className="space-y-3">
                  {form.responsibilities.length === 0 && (
                    <p className="text-xs text-ink-300">
                      No groups yet. Add one to list responsibilities under a
                      heading.
                    </p>
                  )}
                  {form.responsibilities.map((g, idx) => (
                    <div key={idx} className="rounded-xl border border-gray-200 p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Text
                          value={g.title}
                          onChange={(e) => updateRespGroup(idx, "title", e.target.value)}
                          placeholder="Group heading (e.g. Technical Support)"
                        />
                        <button
                          type="button"
                          onClick={() => removeRespGroup(idx)}
                          className="shrink-0 p-2 rounded-lg text-red-500 hover:bg-red-50"
                          title="Remove group"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <Area
                        value={g.items}
                        onChange={(e) => updateRespGroup(idx, "items", e.target.value)}
                        rows={3}
                        placeholder="One responsibility per line"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Field label="Requirements" hint="(one per line)">
                <Area value={form.requirements} onChange={set("requirements")} rows={4} />
              </Field>
              <Field label="Perks & benefits" hint="(one per line)">
                <Area value={form.perks} onChange={set("perks")} rows={3} />
              </Field>
              <Field label="Walk-in details">
                <Area value={form.walkInDetails} onChange={set("walkInDetails")} rows={3} />
              </Field>
              <Field label="Important note">
                <Area value={form.importantNote} onChange={set("importantNote")} rows={2} />
              </Field>
              <Field label="Key skills" hint="(comma or newline separated)">
                <Area value={form.keySkills} onChange={set("keySkills")} rows={2} />
              </Field>
              <Field label="About company">
                <Area value={form.about} onChange={set("about")} rows={3} />
              </Field>
            </section>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black tracking-wide
                           text-white bg-brand-purple hover:brightness-110 active:scale-95 transition-all disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isEditing ? "Save changes" : "Create drive"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-5 py-3 rounded-xl text-sm font-bold text-ink-500 hover:text-ink-900 border border-gray-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          {/* --------------------- existing drives --------------------- */}
          <aside className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-[88px]">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-black text-ink-900">
                  Existing drives
                </h2>
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-1 text-xs font-bold text-brand-purple hover:underline"
                >
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
              </div>

              {listLoading ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-purple" />
                </div>
              ) : drives.length === 0 ? (
                <p className="text-sm text-ink-500 rounded-xl border border-gray-200 bg-white p-4">
                  No drives yet. Create your first one using the form.
                </p>
              ) : (
                <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                  {drives.map((d) => (
                    <li
                      key={d.slug}
                      className={
                        "rounded-xl border bg-white p-3 " +
                        (form.slug === d.slug
                          ? "border-brand-purple ring-1 ring-brand-purple/30"
                          : "border-gray-200")
                      }
                    >
                      <p className="text-sm font-bold text-ink-900 leading-snug line-clamp-2">
                        {d.title}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-500">
                        {d.company} · {d.venue}
                      </p>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span
                          className={
                            "px-2 py-0.5 rounded-full text-[10px] font-black uppercase " +
                            (d.status === "active"
                              ? "bg-brand-green/10 text-brand-green"
                              : "bg-gray-100 text-ink-500")
                          }
                        >
                          {d.status}
                        </span>
                        <Link
                          to={`/walkin-drives/${d.slug}`}
                          className="ml-auto p-1.5 rounded-lg text-ink-500 hover:bg-gray-100"
                          title="View public page"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => startEdit(d)}
                          className="p-1.5 rounded-lg text-brand-purple hover:bg-brand-purple/10"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(d)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
